// Analyses List Route
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { formatDate, cn } from '@/lib/utils';
import { Plus, FileText, Trash2, MoreVertical, Download, Loader2 } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/analyses/')({
  component: AnalysesList,
});

// Map granular workflow states to display tab categories:
//   'idle' → draft
//   'uploading' | 'processing' | 'financial_review' | 'metrics_review' | 'processing_remaining' | 'generating' → in_progress
//   'complete' → completed
// Legacy values are also mapped for backwards compatibility:
//   'draft' → draft, 'in_progress' → in_progress, 'completed' → completed
function getDisplayTab(status: string): 'draft' | 'in_progress' | 'completed' {
  switch (status) {
    case 'idle':
    case 'draft': // legacy
      return 'draft';
    case 'uploading':
    case 'processing':
    case 'financial_review':
    case 'metrics_review':
    case 'processing_remaining':
    case 'generating':
    case 'in_progress': // legacy
      return 'in_progress';
    case 'complete':
    case 'completed': // legacy
      return 'completed';
    default:
      return 'draft';
  }
}

function AnalysesList() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<string>('all');
  const [analysisToDelete, setAnalysisToDelete] = useState<any | null>(null);
  const { isReady } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['analyses'],
    queryFn: () => api.analyses.list(),
    enabled: isReady,
    staleTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.analyses.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analyses'] });
      setAnalysisToDelete(null);
    },
  });

  const confirmDelete = () => {
    if (analysisToDelete) {
      deleteMutation.mutate(analysisToDelete.id);
    }
  };

  const analyses = data?.data || [];

  const filteredAnalyses = filter === 'all'
    ? analyses
    : analyses.filter((a) => getDisplayTab(a.status) === filter);

  const statusCounts = {
    all: analyses.length,
    draft: analyses.filter((a) => getDisplayTab(a.status) === 'draft').length,
    in_progress: analyses.filter((a) => getDisplayTab(a.status) === 'in_progress').length,
    completed: analyses.filter((a) => getDisplayTab(a.status) === 'completed').length,
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Analyses</h1>
          <p className="text-muted-foreground mt-1">
            View and manage your credit analyses
          </p>
        </div>
        <Link
          to="/workflow"
          className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-400 transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Analysis
        </Link>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border">
        {(['all', 'draft', 'in_progress', 'completed'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              filter === status
                ? 'border-accent-500 text-accent-500'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {status === 'all' ? 'All' : status.replace('_', ' ')}
            <span className="ml-2 px-2 py-0.5 bg-muted rounded-full text-xs">
              {statusCounts[status]}
            </span>
          </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="skeleton h-24 rounded-xl" />
          ))}
        </div>
      ) : filteredAnalyses.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xl border border-border">
          <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-xl font-medium mb-2">No analyses found</p>
          <p className="text-muted-foreground mb-6">
            {filter === 'all'
              ? "Get started by creating your first analysis"
              : `No ${filter.replace('_', ' ')} analyses`}
          </p>
          <Link
            to="/workflow"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-400"
          >
            <Plus className="w-5 h-5" />
            Create Analysis
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAnalyses.map((analysis) => (
            <AnalysisRow
              key={analysis.id}
              analysis={analysis}
              onDelete={() => setAnalysisToDelete(analysis)}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {analysisToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => !deleteMutation.isPending && setAnalysisToDelete(null)}
          />

          {/* Modal Content */}
          <div className="relative bg-card border border-border w-full max-w-md rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center gap-3 mb-4 text-destructive">
              <div className="p-2 bg-destructive/10 rounded-full">
                <Trash2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Delete Analysis</h3>
            </div>

            <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
              Are you sure you want to delete the analysis for <span className="font-semibold text-foreground">"{analysisToDelete.businessName || 'Untitled Analysis'}"</span>?
              This will remove the analysis <span className="text-destructive font-bold">permanently</span>. This action cannot be undone.
            </p>

            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                disabled={deleteMutation.isPending}
                onClick={() => setAnalysisToDelete(null)}
                className="px-4 py-2 rounded-xl text-sm font-medium hover:bg-muted transition-colors border border-border disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                disabled={deleteMutation.isPending}
                onClick={confirmDelete}
                className="px-4 py-2 bg-destructive text-white rounded-xl text-sm font-bold hover:bg-destructive/90 transition-all shadow-lg shadow-destructive/20 disabled:opacity-50 flex items-center justify-center min-w-[140px]"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Confirm Deletion
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface AnalysisRowProps {
  analysis: any;
  onDelete: () => void;
}

function AnalysisRow({ analysis, onDelete }: AnalysisRowProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const displayTab = getDisplayTab(analysis.status);

  const statusColors: Record<string, string> = {
    draft: 'bg-muted text-muted-foreground',
    in_progress: 'bg-warning/10 text-warning',
    completed: 'bg-success/10 text-success',
    archived: 'bg-muted text-muted-foreground',
  };

  // States where the workflow is paused waiting for user action
  const needsUserAction = analysis.status === 'financial_review' || analysis.status === 'metrics_review';

  // Handle clicking on an analysis — route based on display tab
  const handleAnalysisClick = () => {
    if (displayTab === 'draft' || displayTab === 'in_progress') {
      // Resume workflow for draft or in-progress analyses
      // Use workflowId to load the workflow context
      navigate({ to: '/workflow/upload', search: { workflowId: analysis.workflowId } });
    } else {
      // View completed analysis details
      navigate({ to: '/analyses/$id', params: { id: analysis.id } });
    }
  };

  // Handle PDF export - simple call to backend which handles everything
  const handleExportPdf = async () => {
    setIsExporting(true);
    setMenuOpen(false);
    
    toast({
      title: 'Generating PDF',
      description: 'Please wait while we create your PDF report...',
    });

    try {
      // Just pass the ID - backend fetches data and generates PDF
      const pdfBuffer = await api.analyses.exportPdf(analysis.id);

      // Create a Blob and trigger download
      const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const sanitizedName = (analysis.businessName || 'Credit_Report').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `CreditNova_${sanitizedName}_${timestamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'PDF Downloaded',
        description: 'Your credit analysis report has been saved as PDF',
      });
    } catch (error: any) {
      console.error('PDF export error:', error);
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to generate PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-6 bg-card rounded-xl border border-border hover:border-accent-500/50 transition-colors group">
      <div className="flex items-center justify-between">
        <button
          onClick={handleAnalysisClick}
          className="flex-1 text-left"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-accent-500/10 text-accent-500 rounded-lg">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg group-hover:text-accent-500 transition-colors">
                {analysis.businessName || 'Untitled Analysis'}
              </h3>
              <p className="text-sm text-muted-foreground">
                Report ID: {analysis.reportId || '—'}
              </p>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <span>Created: {formatDate(analysis.createdAt)}</span>
                {analysis.updatedAt !== analysis.createdAt && (
                  <span>Updated: {formatDate(analysis.updatedAt)}</span>
                )}
              </div>
            </div>
          </div>
        </button>

        <div className="flex items-center gap-4">
          {needsUserAction && (
            <span className="px-3 py-1 text-sm font-medium rounded-full bg-rose-500/10 text-rose-400">
              Review Needed
            </span>
          )}
          <span
            className={cn(
              'px-3 py-1 text-sm font-medium rounded-full capitalize',
              statusColors[displayTab] || statusColors.draft
            )}
          >
            {displayTab.replace('_', ' ')}
          </span>

          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <MoreVertical className="w-5 h-5" />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 w-48 bg-card rounded-lg shadow-lg border border-border z-20">
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      handleAnalysisClick();
                    }}
                    className="flex items-center gap-2 px-4 py-2 w-full hover:bg-muted transition-colors text-left"
                  >
                    <FileText className="w-4 h-4" />
                    {displayTab === 'draft' || displayTab === 'in_progress' ? 'Resume' : 'View Details'}
                  </button>
                  {displayTab === 'completed' && (
                    <button
                      onClick={handleExportPdf}
                      disabled={isExporting}
                      className="flex items-center gap-2 px-4 py-2 w-full hover:bg-muted transition-colors disabled:opacity-50"
                    >
                      {isExporting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {isExporting ? 'Exporting...' : 'Export PDF'}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete();
                    }}
                    className="flex items-center gap-2 px-4 py-2 w-full text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
   